/**
 * Default assessment scales (mirror backend DEFAULT_SCALES). Used as fallback when settings API is unavailable or not yet loaded.
 */
export const DEFAULT_SCALES = {
  criticality: [
    { value: 'MB', label: 'Peripheral' },
    { value: 'B', label: 'Supportive' },
    { value: 'M', label: 'Important' },
    { value: 'A', label: 'Essential' },
    { value: 'MA', label: 'Vital/Strategic' },
  ],
  impact_levels: [
    { value: 1, label: 'Very Low', color: 'bg-green-500' },
    { value: 2, label: 'Low', color: 'bg-blue-400' },
    { value: 3, label: 'Moderate', color: 'bg-yellow-500' },
    { value: 4, label: 'High', color: 'bg-orange-500' },
    { value: 5, label: 'Very High', color: 'bg-red-600' },
  ],
  user_count_levels: [
    { value: 1, label: 'Very Low', desc: '0% to 20% of market/users', color: 'bg-green-500' },
    { value: 2, label: 'Low', desc: '20% to 40% of market/users', color: 'bg-blue-400' },
    { value: 3, label: 'Moderate', desc: '40% to 60% of market/users', color: 'bg-yellow-500' },
    { value: 4, label: 'High', desc: '60% to 80% of market/users', color: 'bg-orange-500' },
    { value: 5, label: 'Very High', desc: '80% to 100% of market/users', color: 'bg-red-600' },
  ],
  frequency_levels: [
    { value: 1, label: 'Very Low', desc: 'Rarely used', color: 'bg-green-500' },
    { value: 2, label: 'Low', desc: 'Occasionally used (weekly)', color: 'bg-blue-400' },
    { value: 3, label: 'Moderate', desc: 'Used daily', color: 'bg-yellow-500' },
    { value: 4, label: 'High', desc: 'Used frequently (multiple times daily)', color: 'bg-orange-500' },
    { value: 5, label: 'Very High', desc: 'Used continuously (24/7)', color: 'bg-red-600' },
  ],
  replaceability_levels: [
    { value: 1, label: 'Very Low', desc: 'Not replaceable', color: 'bg-red-600' },
    { value: 2, label: 'Low', desc: 'Replaceable in >72 hours', color: 'bg-orange-500' },
    { value: 3, label: 'Moderate', desc: 'Replaceable in 24-72 hours', color: 'bg-yellow-500' },
    { value: 4, label: 'High', desc: 'Replaceable in hours', color: 'bg-blue-400' },
    { value: 5, label: 'Very High', desc: 'Immediately replaceable', color: 'bg-green-500' },
  ],
  interdependency_levels: [
    { value: 1, label: 'Very Low', desc: 'No perceived impact or very low impact in 1 other essential service', color: 'bg-green-500' },
    { value: 2, label: 'Low', desc: 'Low impact in up to 2 other essential services', color: 'bg-blue-400' },
    { value: 3, label: 'Moderate', desc: 'Moderate impact in up to 3 other essential services', color: 'bg-yellow-500' },
    { value: 4, label: 'High', desc: 'Significant impact in up to 4 other essential services', color: 'bg-orange-500' },
    { value: 5, label: 'Very High', desc: 'Severe impact in 5 or more other essential services', color: 'bg-red-600' },
  ],
  time_to_impact_levels: [
    { value: 1, label: 'Very Low', desc: 'in less than 4 hours', color: 'bg-red-600' },
    { value: 2, label: 'Low', desc: 'Between 4 and 24 hours', color: 'bg-orange-500' },
    { value: 3, label: 'Moderate', desc: 'Between 24 and 48 hours', color: 'bg-yellow-500' },
    { value: 4, label: 'High', desc: 'Between 48 and 72 hours', color: 'bg-blue-400' },
    { value: 5, label: 'Very High', desc: '> 72 hours', color: 'bg-green-500' },
  ],
  automation_levels: [
    { value: 1, label: 'Very Low', desc: 'Completely manual, no IT/OT dependency' },
    { value: 2, label: 'Low', desc: 'Low automation, mostly manual' },
    { value: 3, label: 'Moderate', desc: 'Moderate automation, can be performed manually if needed' },
    { value: 4, label: 'High', desc: 'Mostly automated, manual contingency exists' },
    { value: 5, label: 'Very High', desc: 'Fully automated, no manual contingency' },
  ],
  cyberattack_impact_levels: [
    { value: 1, label: 'Very Low', desc: 'Minimal impact' },
    { value: 2, label: 'Low', desc: 'Limited impact' },
    { value: 3, label: 'Moderate', desc: 'Significant impact' },
    { value: 4, label: 'High', desc: 'Severe impact' },
    { value: 5, label: 'Very High', desc: 'Critical impact' },
  ],
};

/** Matrix order: 1→MB, 2→B, 3→M, 4→A, 5→MA */
const IMPACT_ORDER = ['mb', 'b', 'm', 'a', 'ma'];

/**
 * Get scale levels array for a scale key. Uses defaults if scales is null/undefined or key missing.
 */
export function getScaleLevels(scales, scaleKey) {
  const s = scales && scales[scaleKey];
  if (Array.isArray(s) && s.length) return s;
  return DEFAULT_SCALES[scaleKey] || [];
}

/**
 * Build { mb: 'Peripheral', ... } from settings.scales.criticality.
 */
export function getCriticalityLabels(scales) {
  const arr = getScaleLevels(scales, 'criticality');
  const out = {};
  arr.forEach((item) => {
    const k = (item.value || '').toString().toLowerCase();
    if (k) out[k] = item.label || item.value;
  });
  return out;
}

/**
 * Build level labels for impact/automation (matrix keys ma, a, m, b, mb). Uses impact_levels: index 0→mb, 4→ma.
 */
export function getLevelLabels(scales) {
  const arr = getScaleLevels(scales, 'impact_levels');
  const out = {};
  IMPACT_ORDER.forEach((key, i) => {
    out[key] = (arr[i] && arr[i].label) || key;
  });
  return out;
}

function needsWhiteText(color) {
  if (!color) return false;
  return color.includes('red') || color.includes('orange');
}

/**
 * Build level colors for matrix/badges (bg + text). Uses impact_levels[].color; index 0→mb, 4→ma.
 */
export function getLevelColors(scales) {
  const arr = getScaleLevels(scales, 'impact_levels');
  const out = {};
  IMPACT_ORDER.forEach((key, i) => {
    const c = arr[i] && arr[i].color;
    const textClass = c && needsWhiteText(c) ? ' text-white' : ' text-gray-900';
    out[key] = c ? `${c}${textClass}` : (key === 'ma' || key === 'a' ? 'bg-gray-600 text-white' : 'bg-gray-400 text-gray-900');
  });
  return out;
}

/**
 * Same as level colors; criticality row colors use the same scale (Vital→Peripheral).
 */
export function getCriticalityRowColors(scales) {
  return getLevelColors(scales);
}

/**
 * Matrix cell background only (no text class). Defaults for when scale has no color.
 */
const DEFAULT_MATRIX_BG = { ma: 'bg-red-600', a: 'bg-orange-500', m: 'bg-amber-400', b: 'bg-yellow-300', mb: 'bg-amber-200' };
const DEFAULT_MATRIX_TEXT = { ma: 'text-white', a: 'text-white', m: 'text-gray-900', b: 'text-gray-900', mb: 'text-gray-900' };

export function getMatrixCellBg(scales) {
  const arr = getScaleLevels(scales, 'impact_levels');
  const out = {};
  IMPACT_ORDER.forEach((key, i) => {
    const c = arr[i] && arr[i].color;
    out[key] = c || DEFAULT_MATRIX_BG[key];
  });
  return out;
}

export function getMatrixCellText(scales) {
  const arr = getScaleLevels(scales, 'impact_levels');
  const out = {};
  IMPACT_ORDER.forEach((key, i) => {
    const c = arr[i] && arr[i].color;
    out[key] = (c && needsWhiteText(c)) ? 'text-white' : 'text-gray-900';
  });
  return out;
}

export function getDefaultMatrixCellBg() {
  return { ...DEFAULT_MATRIX_BG };
}
export function getDefaultMatrixCellText() {
  return { ...DEFAULT_MATRIX_TEXT };
}

const DEFAULT_SCORE_BAR_COLORS = { 1: 'bg-green-500', 2: 'bg-blue-400', 3: 'bg-yellow-500', 4: 'bg-orange-500', 5: 'bg-red-600' };

/**
 * Bar fill colors for numeric score 1–5 (e.g. impact/automation bars). Uses impact_levels: index 0 → score 1 (Very Low = green), 4 → score 5 (Very High = red).
 */
export function getScoreBarColors(scales) {
  const arr = getScaleLevels(scales, 'impact_levels');
  const out = {};
  for (let i = 0; i < 5; i++) {
    const score = i + 1;
    out[score] = (arr[i] && arr[i].color) || DEFAULT_SCORE_BAR_COLORS[score];
  }
  return out;
}
